import React from 'react'
import { Dialog as MaterialModal, useMediaQuery } from '@mui/material'
import styled, { useTheme } from 'styled-components'
import CloseIcon from '@mui/icons-material/Close'
import PrimaryButton from 'components/atoms/PrimaryButton'
import Button from 'components/atoms/Button'
import useBodyScrollLock from 'hooks/useBodyScrollLock'

const Modal = props => {
  const {
    title,
    children,
    withSubmit,
    withCancel,
    submitButtonText = 'Submit',
    cancelButtonText = 'Cancel',
    onCancel = () => {},
    onSubmit = () => {},
    disabled,
    onClose,
    minWidthDesktop = '435px',
    maxWidthDesktop = '600px',
    dialogPaperSx,
    disableBodyScrollLock = false,
    ...materialModalProps
  } = props

  const theme = useTheme()
  const isMobile = useMediaQuery('(max-width:768px)')

  // Filter out custom props that shouldn't be passed to MaterialModal
  const {
    withSubmit: _withSubmit,
    withCancel: _withCancel,
    submitButtonText: _submitButtonText,
    cancelButtonText: _cancelButtonText,
    onCancel: _onCancel,
    onSubmit: _onSubmit,
    disabled: _disabled,
    title: _title,
    minWidthDesktop: _minWidthDesktop,
    maxWidthDesktop: _maxWidthDesktop,
    dialogPaperSx: _dialogPaperSx,
    children: _children,
    ...filteredProps
  } = materialModalProps

  useBodyScrollLock(Boolean(filteredProps.open) && !isMobile && !disableBodyScrollLock)

  return (
    <MaterialModal
      {...filteredProps}
      onClose={onClose}
      fullScreen={isMobile}
      sx={{
        zIndex: 99999,
        '& .MuiDialog-paper': {
          margin: isMobile ? 0 : 4,
          width: isMobile ? '100vw' : 'auto',
          height: isMobile ? '100dvh' : 'auto',
          maxHeight: isMobile ? 'none' : 'calc(100% - 64px)',
          minWidth: isMobile ? '100%' : minWidthDesktop,
          maxWidth: isMobile ? '100%' : maxWidthDesktop,
          borderRadius: isMobile ? 0 : '12px',
          backgroundColor: theme.colors.white,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          ...(dialogPaperSx || {})
        }
      }}
    >
      <StyledContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
          <StyledCloseIcon onClick={onClose} />
        </ModalHeader>

        <ModalBody>
          {children}
        </ModalBody>

        {
          (withSubmit || withCancel) && (
            <ButtonsWrapper>
              {
                withCancel && (
                  <CancelButton variant='outlined' onClick={onCancel}>
                    {cancelButtonText}
                  </CancelButton>
                )
              }
              {
                withSubmit && (
                  <SubmitButton onClick={onSubmit} disabled={disabled}>
                    {submitButtonText}
                  </SubmitButton>
                )
              }
            </ButtonsWrapper>
          )
        }
      </StyledContainer>
    </MaterialModal>
  )
}

export default Modal

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[500]};
  flex-shrink: 0;
`

const ModalTitle = styled.div`
  font-size: 16px; /* Font size original */
  font-weight: bold;
  color: ${({ theme }) => theme.colors.haiti};
  opacity: .5;
`

const StyledCloseIcon = styled(CloseIcon)`
  font-size: 20px !important;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  padding: 4px;

  &:hover {
    color: ${({ theme }) => theme.colors.black};
  }
`

const ModalBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 20px;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const ButtonsWrapper = styled.div`
  padding: 16px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.grey[600]};
  background: ${({ theme }) => theme.colors.white};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  padding-bottom: max(16px, env(safe-area-inset-bottom));

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    gap: 12px;
  }
`

const CancelButton = styled(Button)`
  @media (max-width: 480px) {
    width: 100%;
  }
`

const SubmitButton = styled(PrimaryButton)`
  margin-left: 0 !important;

  @media (max-width: 480px) {
    width: 100%;
  }
`
